/**
 * ログイン直後の画面（ホーム）の出し分け。純関数だけ（テストは homeFlow.test.ts・画面は pages/HomeStart.tsx）。
 * 2026-09-28 creator/home-redesign（founder の感想・最優先）。
 *
 * WHY 作り直したか: 以前の最初の画面は「プロジェクトの一覧 + 帯 2 本（メール未確認・前払い）+ 空の案内」で、
 *   founder は「いきなり情報量が多い」「機能を選んで使っていくサービスに見える」「ホスティング・課金・DB が揃っていて、
 *   あとはバイブコーディングで何でも作れる、という印象が一切ない」と感じた。
 *   目指す印象（MASTER-PLAN §1c「フラットなバックエンド」）= 公開・データ・お金はもう用意されている。あとは Claude Code に話すだけ。
 * だから最初の画面は: 3 つの柱（どれも「用意済み」・ON/OFF の一覧は出さない）→ 次の一歩を 1 つだけ大きく。
 *   次の一歩は「段」の並び（メール確認 → Claude Code をつなぐ → 入金 → 話す → 公開）で、いまの段だけ開いて主なボタンを 1 つ出す。
 *   メール未確認・前払い 0 は、以前は上の帯で出していたが、ホームでは流れの 1 段として出す（帯と二重にしない）。
 */

export type StepId = 'verify' | 'connect' | 'topUp' | 'talk' | 'publish';
export type StepState = 'done' | 'current' | 'todo';
export interface Step {
  id: StepId;
  state: StepState;
}

/**
 * prepaidGate.ts の prepaidBand() の結果（'start' = まだ入金していない・'owed' / 'empty' = 使い切って止まっている・
 * 'invitePending' = 招待クレジットがメールの確認待ち・'low' / 'lowInvite' = 少ないが動いている）。
 * 入金の段（topUp）になるのは 'start' だけ（2026-09-29 creator/launch-close-2・Money の前払いの道 17）:
 *   招待が入った人は 'start' にならない（入っている間は canSpend true = null か low、使い切ったら 'empty' = 帯の警告）、
 *   確認待ちの人は 'invitePending'（確認の段が「確認すると {amount} が入ります」を言う）= どちらにも「$5 から入金」の段を出さない
 */
export type PrepaidKind = import('./prepaidGate').PrepaidBandKind | null;

export interface HomeInput {
  /** false = 未確認。undefined（古いサーバー）は確認済みとして扱う（帯と同じ: 推測で「確認して」と言わない） */
  emailVerified: boolean | undefined;
  prepaid: PrepaidKind;
  /**
   * AI がつながっているか = 生きている委任トークン（kdt_）が 1 つ以上ある、または CLI のログインが生きている（GET /v1/developers/delegations・
   * connectedFrom。CLI を数えるのは 2026-11-05 core/first5 から）。
   * null = 分からない（読み込み中・未確認で読んでいない・失敗）→ つながっていないものとして「つなぐ」を出す
   * （すでにつないでいる人に 1 行を見せても害は無いが、つないでいない人に隠すと最初の一歩が消える）
   */
  connected: boolean | null;
  /**
   * どの道でつながったか（connectedFrom の via・2026-11-05 core/first5）。'cli' = CLI のログインだけ（MCP の許可はまだ）。
   * 済みの段に「MCP の道具も使うなら、AI を再起動して許可」の 1 行を出すかだけに使う。省略 = 今までどおり（何も足さない）
   */
  connectedVia?: ConnectedVia;
  projectCount: number;
  /**
   * 招待クレジットの約束の額（GET /v1/money/prepaid の invite.granted・セント）と通貨。無い・読めないなら null / 省略。
   * 確認待ち（prepaid が 'invitePending'）のとき、確認の段（verify）の文を「確認すると {amount} の招待クレジットが入ります」にする（inviteVerifyAmount）
   */
  invite?: { granted: number; currency: string } | null;
}

/** 確認の段の文を招待つきにするか。確認待ちの帯の種類（'invitePending'）で、額が読めるときだけ。null = 普段の文 */
export function inviteVerifyAmount(i: HomeInput): { granted: number; currency: string } | null {
  return i.prepaid === 'invitePending' && i.invite && i.invite.granted > 0 ? i.invite : null;
}

/**
 * 段の並び。含める段:
 *   verify … メールが未確認のときだけ（確認済みの人に「確認済み ✓」を見せても情報が増えるだけ）
 *   connect … いつも（MCP はメール確認が要る = verify の後）
 *   topUp … まだ一度も入金していない（'start'）ときだけ。'owed' / 'empty'（使い切って止まっている）は流れではなく
 *           警告なので、今までどおり上の帯（PrepaidBand）で出す
 *   talk … ゲームが 1 つでもあれば済み（話して作った = プロジェクトができた、とみなす）
 *   publish … いつも。公開したかはここでは分からない（環境ごとのデプロイを全部読まない）ので「済み」にはしない
 * WHY 入金を「つなぐ」の後に置くか: つなぐのに残高は要らないが、ゲームの作成・デプロイは残高が要る（402）。
 *   最初に「お金を入れて」から始めるより「つなぐ → 入れる → 作る」の方が、何のための入金か分かる
 */
export function homeSteps(i: HomeInput): Step[] {
  const ids: Array<{ id: StepId; done: boolean }> = [];
  if (i.emailVerified === false) ids.push({ id: 'verify', done: false });
  ids.push({ id: 'connect', done: i.connected === true });
  if (i.prepaid === 'start') ids.push({ id: 'topUp', done: false });
  ids.push({ id: 'talk', done: i.projectCount > 0 });
  ids.push({ id: 'publish', done: false });
  let currentTaken = false;
  return ids.map(({ id, done }) => {
    if (done) return { id, state: 'done' as const };
    if (!currentTaken) {
      currentTaken = true;
      return { id, state: 'current' as const };
    }
    return { id, state: 'todo' as const };
  });
}

/**
 * ゲームが既にある人に出す「次の一歩」（1 つだけ・無ければ null）。
 * WHY 準備の段（確認・つなぐ・入金）だけか: ゲームがある人に「話す」「公開する」を毎回大きく見せるのはうるさい。
 *   準備が欠けているとき（例: 別の端末で作ったが Claude Code はまだ）だけ知らせる
 */
export function nextSetupStep(i: HomeInput): StepId | null {
  const cur = homeSteps(i).find((s) => s.state === 'current');
  return cur && (cur.id === 'verify' || cur.id === 'connect' || cur.id === 'topUp') ? cur.id : null;
}

// ---------------------------------------------------------------- 画面の形（A / B / C）

/**
 * ホームの形。**ここ 1 か所を変えるだけで切り替わる**。既定は C。
 *   'a' … 言い切りの見出し（home.title）＋3 つの柱を 3 列 ＋「はじめの一歩」の段（最初の案・ef5181a5）
 *   'b' … 問いかけの見出し（home.b.title）＋1 行の入力欄 → 頼み方の文とコピー（ca5b1d3a）
 *   'c' … 道具としての素直な画面（既定・2026-09-28 管理の方針）: 見出しは場所の名前「ゲーム」だけ・説明 1 行・
 *         番号つきの手順（つなぐ → [確認] → [入金] → 頼み方の例）・「ゲームを作る」のボタン。柱・「用意済み」・決め台詞は出さない
 * 経緯: founder の感想「『もう、全部そろっています』はダサい」（A）→「『何を作る？』は意味がわからん。キャッチコピーみたいなことは要らない」（B）。
 *   管理の方針: ダッシュボードから宣伝の言葉と見出しの決め台詞をなくし、Vercel・Cloudflare のように見出しは場所の名前だけにする。
 *   2026-09-28 founder の方針（MASTER-PLAN §0）で「ゲーム」→「プロジェクト」（アプリでも何でも）。頼み方の例もアプリを混ぜた
 *   言葉はどれも、何が起きるかをそのまま書く説明にする。A / B は見比べ用に残している（消すときは HomeParts / HomeAsk と en/ja.home.ts の a・b の分）
 */
export type HomeVariant = 'a' | 'b' | 'c';
export const HOME_VARIANT: HomeVariant = 'c';

/** 形ごとに、画面のどの部品を出すか（HomeStart.tsx はこれを見て組み立てるだけ = 切り替えの中身をテストできる） */
export interface HomeLayout {
  /** statement = 言い切り（A）・question = 問いかけ＋入力欄（B）・page = 場所の名前だけ（C） */
  hero: 'statement' | 'question' | 'page';
  pillars: 'columns' | 'line' | 'none';
  /** flow = いまの段だけ開く段の並び（A）・setup = 準備だけを控えめに（B）・numbered = 番号つきの手順を全部開いて素直に（C） */
  steps: 'flow' | 'setup' | 'numbered';
  /** step = 段の 1 つとして（A・C）・line = 入力欄の下の小さな 1 行（B） */
  connect: 'step' | 'line';
}
export function homeLayout(v: HomeVariant): HomeLayout {
  if (v === 'a') return { hero: 'statement', pillars: 'columns', steps: 'flow', connect: 'step' };
  if (v === 'b') return { hero: 'question', pillars: 'line', steps: 'setup', connect: 'line' };
  return { hero: 'page', pillars: 'none', steps: 'numbered', connect: 'step' };
}

/**
 * C の手順。ゲームが 0 の人: つなぐ・[確認]・[入金]・頼み方の例（ask）。済んだもの（つないだ）は done。
 * ゲームがある人: 欠けている準備（確認・つなぐ・入金）だけ（無ければ空 = 手順を出さない。一覧が主）。
 * WHY 未確認なら「確認」を「つなぐ」の前に置くか: 管理の指示は「1. つなぐ 2. 入金（未確認ならその前に確認）」だが、
 *   メールが未確認だと MCP の接続（kdt_ の発行）も 403 になる（サーバーの SEC-DEVLOGIN-01）。つなぐを先に見せると押しても失敗するので、確認を先頭にする
 */
export type StepCId = 'verify' | 'connect' | 'topUp' | 'ask';
export function homeStepsC(i: HomeInput): Array<{ id: StepCId; done: boolean }> {
  const out: Array<{ id: StepCId; done: boolean }> = [];
  if (i.emailVerified === false) out.push({ id: 'verify', done: false });
  out.push({ id: 'connect', done: i.connected === true });
  if (i.prepaid === 'start') out.push({ id: 'topUp', done: false });
  if (i.projectCount > 0) return out.filter((s) => !s.done);
  out.push({ id: 'ask', done: false });
  return out;
}

/**
 * C の手順の見せ方（2026-10-14 gtm/onboarding・活性化の「最初の 1 歩を 1 つだけ大きく」）。
 *   done  … 済み（チェックと「済み」だけ）
 *   open  … いまの段 = 最初のまだの段。中身（ボタン・コピーの文・例）を全部出す。**いつも 1 つだけ**
 *   later … その先の段。題だけを薄く出す（何が来るかは分かるが、押せる物は出さない）
 * WHY: 10/14 の本番の数字で、招待で登録した 9 人のうち AI をつないだのは 2 人。登録直後の画面は 4 段を全部開いて
 *   ボタンが 7 つ・コピーの欄が 5 つ並んでいた（docs/teams/gtm/screenshots/before-01-unverified.png）。
 *   しかもメール未確認のうちは「つなぐ」の文を貼っても 403 で失敗する（SEC-DEVLOGIN-01）= 押せる物を見せると失敗を招く。
 *   A の flow（いまの段だけ開く）と同じ考えを C にも入れる。どの段をどの順で出すか（homeStepsC）は変えない
 */
export type StepCView = 'done' | 'open' | 'later';
export function stepViewsC(steps: Array<{ id: StepCId; done: boolean }>): StepCView[] {
  let opened = false;
  return steps.map((s) => {
    if (s.done) return 'done';
    if (opened) return 'later';
    opened = true;
    return 'open';
  });
}

/**
 * つながるのを待つ間、委任（GET /v1/developers/delegations）を読み直す間隔（ミリ秒）。null = 読み直さない。
 * WHY: 文を AI に貼ったあと、画面が変わらないと「つながったのか」が分からず止まる（活性化の手前で一番多く止まる所）。
 *   いまの段が「つなぐ」で、メールが確認済み（未確認だと 403 なので読まない）、まだつながっていないときだけ 5 秒ごと。
 *   つながったら止める（読み続ける理由が無い・サーバーの負担を増やさない）。画面が隠れている間は useResource が読まない
 */
export const CONNECT_POLL_MS = 5_000;

/**
 * GET /v1/developers/delegations の応答から「AI がつながったか」を決める（ホーム・概要の画面が同じ式を使う）。
 * 2026-11-05 core/first5（指示書 docs/teams/core/tasks/first5.md の 3）:
 *   以前は委任トークン（kdt_ = MCP の許可）が 1 本以上あるかだけ。だが AI（Claude Code・Codex・Cursor）が `kumodeck connect` を打って
 *   ブラウザで Allow した時点で、AI は CLI で作る・試す・公開するまで全部できる（11/4 の通し docs/teams/core/signup-test/findings.md は
 *   全部 CLI で公開まで通った）。それでもホームは「Waiting for your AI to connect」のまま = 新しい人が「つながっていない」と誤解して止まる。
 *   本体が CLI のログイン（developer_sessions.origin = 'cli_grant'）の有無を cliSignedIn で返すようにしたので、それも「つながった」に数える。
 * 戻り値: connected = null（まだ読めていない）/ true / false。via = 'mcp'（kdt_ がある）/ 'cli'（CLI のログインだけ）/ null。
 *   cliSignedIn が無い（古い本体）ときは今までどおり kdt_ だけで決める（推測で「つながった」と言わない）
 */
export type ConnectedVia = 'mcp' | 'cli' | null;
export function connectedFrom(data: { delegations?: unknown[]; cliSignedIn?: unknown } | null | undefined): { connected: boolean | null; via: ConnectedVia } {
  if (!data) return { connected: null, via: null };
  if ((data.delegations?.length ?? 0) > 0) return { connected: true, via: 'mcp' };
  if (data.cliSignedIn === true) return { connected: true, via: 'cli' };
  return { connected: false, via: null };
}
export function connectPollMs(i: Pick<HomeInput, 'emailVerified' | 'connected'>): number | null {
  if (i.emailVerified === false || i.connected === true) return null;
  return CONNECT_POLL_MS;
}

/**
 * メールの確認を待つ間、自分の情報（GET /v1/developers/me）を読み直す間隔。null = 読み直さない。
 * WHY: 確認のリンクは別のタブで開く。元のタブ（ホーム）は「メールを確認して」のまま止まって見える。
 *   未確認のときだけ 10 秒ごとに読み、確認済みになったら次の段（つなぐ）を開く
 */
export const VERIFY_POLL_MS = 10_000;
export function verifyPollMs(emailVerified: boolean | undefined): number | null {
  return emailVerified === false ? VERIFY_POLL_MS : null;
}

/**
 * B で入力欄の下に出す準備の段（順: メール確認 → 入金）。「つなぐ」は別の 1 行で出すのでここには入れない。
 * 入金はメールの確認が済むまで押しても 403 になる（PrepaidBand の WHY と同じ）ので、未確認のうちは確認だけを出す
 */
export function setupNotices(i: HomeInput): Array<'verify' | 'topUp'> {
  if (i.emailVerified === false) return ['verify'];
  return i.prepaid === 'start' ? ['topUp'] : [];
}

// ---------------------------------------------------------------- B: 頼み方の文

/** 入力の上限（文字）。Claude Code への 1 文としては十分・画面が崩れない長さ */
export const ASK_MAX = 200;

/**
 * 入力（作りたいもの）→ Claude Code にそのまま貼れる頼み方の文。空（空白だけ）なら null（画面は例の文を出す）。
 * template は i18n の home.askTemplate（{what} に入力が入る）。
 * 整え方と WHY:
 *   - 改行・タブ・連続した空白は 1 つの空白に: 貼ったときに Claude Code のプロンプトで途中送信・複数行にならないように
 *   - 全体を括った引用符（" ' “ ” 「 」 『 』）を外す: テンプレートが自分で括るので二重にしない
 *   - 中の " と 『』 は ' と 「」 に: テンプレートの括り（en は "…"・ja は『…』）が途中で閉じて読めなくならないように
 *   - ASK_MAX 文字で切って … を付ける（入力欄にも maxLength があるが、貼り付けは超えうる）
 *   - 置き換えは関数で行う（入力の $& 等が String.replace の特殊な記号として働かないように）
 * 入力はこの画面の中だけで使う: サーバーに送らない・保存しない（localStorage も使わない）
 * 差し替えの予定（2026-09-28 管理判断）: 入口の Skill「start」（作りたいものを聞く → テンプレート → Kumo につなぐ → 入金と確認 → 公開）が
 *   できたら、文を start を起点にする書き方に変える。変えるのは **この関数と i18n の home.askTemplate の 2 か所だけ**（画面は触らない）。
 *   文にはエージェントの製品名を入れない（どのエージェントに貼っても通じる形・wording.test.ts が止める）
 */
export function buildAsk(raw: string, template: string): string | null {
  let v = raw.replace(/\s+/g, ' ').trim();
  // 両端がどちらも引用符のときだけ外す（片側だけ外すと『魔王』を倒す… の頭の『が消えて括りが崩れる・テストで発見）
  const Q = `"'“”‘’「」『』`;
  while (v.length >= 2 && Q.includes(v[0]!) && Q.includes(v[v.length - 1]!)) v = v.slice(1, -1).trim();
  if (!v) return null;
  v = v.replace(/["“”]/g, "'").replace(/『/g, '「').replace(/』/g, '」');
  if ([...v].length > ASK_MAX) v = [...v].slice(0, ASK_MAX).join('').trimEnd() + '…';
  return template.replace('{what}', () => v);
}

// ---------------------------------------------------------------- URL の検査

/**
 * 画面に出してコピーさせる URL として使えるか。http(s) の絶対 URL で、シェルで特別な意味を持つ文字を含まないものだけ。
 * WHY 厳しくするか: この値はクリエイターがそのままターミナルに貼る 1 行になる。サーバーの env の書き間違い
 *   （空白・引用符・`;` など）がそのまま貼られると、別のコマンドとして動きうる。形が怪しければ「無い」として扱い、ガイドへのリンクに落とす
 */
export function safeHttpUrl(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const s = raw.trim();
  if (!s || /[\s'"`$\\;&|<>(){}!*]/.test(s)) return null;
  let u: URL;
  try {
    u = new URL(s);
  } catch {
    return null;
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
  if (u.username || u.password) return null; // 資格情報の入った URL は案内に出さない
  return s.replace(/\/+$/, '');
}

/**
 * KUMO_MCP_URL（GET /v1/platform/info の mcpUrl）から、エージェントに渡す MCP の接続先を作る。
 * WHY: 運用の決め事では KUMO_MCP_URL は MCP の Worker の**オリジンだけ**（`https://mcp.<A>`・docs/OPERATIONS.md。
 *   本体は OAuth の戻り先の検査にオリジンを使い、check:prod が MCP 側の MCP_PUBLIC_URL と突き合わせる）。
 *   ところが MCP が応じるのは `/mcp`（apps/mcp の MCP_ROUTE）だけ = オリジンのまま貼らせるとつながらない（home-copy が 2026-09-28 に発見）。
 *   運用の値を変えずに済むよう、パスが無い（`/` だけ）ときはここで `/mcp` を足す。パスが書いてあればそのまま使う（`…/mcp` を入れた環境も動く）
 */
export const MCP_PATH = '/mcp';
export function mcpEndpoint(raw: unknown): string | null {
  const s = safeHttpUrl(raw);
  if (!s) return null;
  const u = new URL(s);
  if (u.search || u.hash) return s;
  return u.pathname === '' || u.pathname === '/' ? `${s}${MCP_PATH}` : s;
}

// つなぐ（エージェントのタブ AGENT_CONNECT・コピペの頼み文 connectAsk・サイトの MCP のページの URL mcpGuideUrls）は
// 2026-10-11 console/one-connect で消した。どの AI にも貼る 1 つの文になった（webConnect.ts connectPromptFor・文の正本は apps/site/src/connectPrompt.mjs）。
// エージェントごとの 1 行の表の正は、引き続きサイトの「AI エージェントから使う（MCP）」のページ（content/<lang>/claude-code.md）と docs/research/2026-09-28-agent-mcp.md

/**
 * 最初のテスト版のカード（ホームの一番上・2026-10-15 gtm/onboarding・onboarding-plan.md §5 の 9）。出すなら遊べる URL、出さないなら null。
 * 出す条件: いちばん新しいプロジェクトのテスト版（development）に動いている版（active）があり、URL の形が正しく、
 *   みんなに公開（production の active）がまだ。production を読めていない（null = 読み込み中・失敗）ときは出さない
 *   （公開済みの人に一瞬「まだテスト版」を見せない）。
 * WHY: 活性化の瞬間（onboarding-plan.md §1 =「最初のゲームがテスト版の URL で動いた」）を、ホームでも大きく見せる。
 *   URL は AI がチャットで返すが、ダッシュボードに戻った人が「どこで遊べるのか」「次は何か（みんなに公開）」を迷わないように
 */
export function firstTestUrl(
  dev: { url?: unknown; active?: unknown } | null | undefined,
  prod: { active?: unknown } | null | undefined
): string | null {
  if (!dev || !dev.active || !prod || prod.active) return null;
  return safeHttpUrl(dev.url);
}
